'use client';
import React, { useState, useEffect } from 'react';
import Input from '@/components/Input';
import { IRepositoryBranch } from '@/lib/repositoryBranchesApi';
import { ICommit } from '@/types';

import * as Styled from './styled';
import Custom from './CustomComponents';
import override from './styleOverrides';

type OptionItem = {
  value: string;
  label: string;
  type: 'branch' | 'commit';
  branch?: IRepositoryBranch;
  commit?: ICommit;
  isDisabled: boolean;
};

type BranchSelectorProps = {
  name?: string;
  label?: string;
  branches: IRepositoryBranch[];
  commit?: ICommit;
  menuIsOpen?: boolean;
  selected?: string;
  disabledBranchNames?: string[];
  onChange: (selected: string) => void;
};

const getOptionsList = (
  branches: IRepositoryBranch[],
  disabledBranchNames: string[],
  selectedRef: string,
  commit?: ICommit,
): OptionItem[] => {
  let options: OptionItem[] = branches.map((branch) => ({
    value: branch.name,
    label: branch.name,
    type: 'branch',
    branch,
    isDisabled: disabledBranchNames.includes(branch.name),
  }));

  const selectedBranch = branches.find((branch) => branch.name === selectedRef);

  // if the selected branch is not the same as the commit, add the commit to the options
  if (!!commit && selectedBranch?.commit.id !== commit.id) {
    options.push({ value: commit.id, label: commit.id, type: 'commit', commit, isDisabled: false } as OptionItem);
  }

  // sort by commit timestamp
  options.sort(
    (a, b) =>
      new Date(b.branch?.commit?.timestamp || '').getTime() - new Date(a.branch?.commit?.timestamp || '').getTime(),
  );

  // sort selected branch to the top
  options.sort(function (x, y) {
    return x.value == selectedRef ? -1 : y.value == selectedRef ? 1 : 0;
  });
  return options;
};

const BranchSelector: React.FC<BranchSelectorProps> = ({
  name = 'selected-branch',
  label,
  branches,
  commit,
  menuIsOpen,
  selected = '',
  disabledBranchNames = [],
  onChange,
}) => {
  const [selectedRef, setSelectedRef] = useState<string>(selected);

  useEffect(() => {
    if (selectedRef) {
      onChange(selectedRef);
    }
  }, [onChange, selectedRef]);

  const options = getOptionsList(branches, disabledBranchNames, selectedRef, commit);

  const hasNoBranches = branches.length === 0;
  const placeholder = hasNoBranches ? 'No branches' : 'Select a branch...';

  return label ? (
    <Styled.BranchSelector>
      <Input.Select
        label={label}
        name={name}
        options={options}
        selectedValue={selectedRef}
        onChange={setSelectedRef}
        disabled={hasNoBranches}
        menuIsOpen={menuIsOpen}
        isSearchable={false}
        customComponents={{ Option: Custom.Option, SingleValue: Custom.SingleValue, Placeholder: Custom.Placeholder }}
        placeholder={placeholder}
        classNames={{ menuList: () => 'branch-selector-menu-list' }}
        styles={{ control: override.controlStyles, menu: override.menuStyles }}
      ></Input.Select>
    </Styled.BranchSelector>
  ) : (
    <Styled.BranchSelector>
      <Input.SelectControl
        name={name}
        options={options}
        selectedValue={selectedRef}
        onChange={setSelectedRef}
        disabled={hasNoBranches}
        menuIsOpen={menuIsOpen}
        isSearchable={false}
        customComponents={{ Option: Custom.Option, SingleValue: Custom.SingleValue, Placeholder: Custom.Placeholder }}
        placeholder={placeholder}
        classNames={{ menuList: () => 'branch-selector-menu-list' }}
        styles={{ control: override.controlStyles, menu: override.menuStyles }}
      ></Input.SelectControl>
    </Styled.BranchSelector>
  );
};

export default BranchSelector;
